import * as React from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Small icon-only clipboard-copy affordance, composed wherever a raw value
// (id, hash, address, JSON payload) needs a one-click copy without hand-
// rolling clipboard logic per call site. Feedback is both visual (icon swap
// for ~1.5s) and a toast confirmation — no other external state. `className`
// is an escape hatch for dense contexts (e.g. inline next to a table cell's
// value) that need a smaller footprint than the default `size="icon"` (h-9 w-9).

export interface CopyButtonProps {
  value: string;
  label?: string;
  className?: string;
}

const FEEDBACK_DURATION_MS = 1500;

export function CopyButton({ value, label = "Copy to clipboard", className }: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  async function handleClick() {
    await navigator.clipboard.writeText(value);
    toast.success("Copied to clipboard");
    if (!mountedRef.current) return;
    setCopied(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (mountedRef.current) setCopied(false);
    }, FEEDBACK_DURATION_MS);
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      onClick={handleClick}
      className={cn("shrink-0", className)}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}
